/**
 * O aviso da fila de triagem (`TP-037`): há e-mail parado que NÃO está nesta fila.
 *
 * Um e-mail cuja triagem falhou não tem ticket e, por isso, não aparece aqui — a fila
 * parece limpa justamente quando há trabalho esperando. A faixa existe para essa
 * pessoa não concluir "não há nada".
 *
 * Três estados, todos honestos:
 * - com falha aberta: quantos e-mails, de qual etapa, e quantos já esgotaram as tentativas;
 * - sem falha: nada (nem o link — não há para onde levar);
 * - o resumo não respondeu: uma linha neutra. Esconder a faixa por erro seria indistinguível
 *   de "sem falha", que é o contrário do que ela existe para dizer.
 *
 * A fila de triagem NUNCA espera nem depende desta consulta.
 */

import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Link as LinkDeRota } from 'react-router-dom';

import { textoDeLeituraDesistida, textoDeLeituraSemAgente } from '../../shared/falhasDeEmail/avisoDeLeitura';
import { opcoesDoResumoDasFalhasDeEmail } from '../../shared/falhasDeEmail/resumo';
import { useIdentidadeDoShell } from '../../shared/layout/useIdentidadeDoShell';
import { textoDoAviso } from './textoDoAviso';

const CAMINHO_DAS_FALHAS = '/falhas-de-email';
const ROTULO_DO_LINK = 'Ver falhas de e-mail';

function LinkParaAsFalhas(): React.JSX.Element {
  return (
    <Link component={LinkDeRota} to={CAMINHO_DAS_FALHAS} variant="body2" underline="hover" sx={{ fontWeight: 600 }}>
      {ROTULO_DO_LINK}
    </Link>
  );
}

export function FaixaDeFalhasDeEmail(): React.JSX.Element {
  const consulta = useQuery(opcoesDoResumoDasFalhasDeEmail());
  const nucleoId = useIdentidadeDoShell((estado) => estado.nucleoId);
  const resumo = consulta.data;
  const doNucleo = nucleoId !== null;

  return (
    // A região fica SEMPRE montada: leitor de tela só anuncia mudança dentro de uma região viva que
    // já existia, não uma que nasce já com o texto. Sem conteúdo ela não ocupa espaço.
    <Stack role="status" aria-live="polite" spacing={1} sx={{ '&:not(:empty)': { mt: 1.5 } }}>
      {consulta.isError ? (
        <Stack direction="row" flexWrap="wrap" columnGap={1.5} rowGap={0.25} alignItems="baseline">
          <Typography variant="body2" color="text.secondary">
            Não foi possível verificar falhas de e-mail
          </Typography>
          <LinkParaAsFalhas />
        </Stack>
      ) : null}

      {resumo?.caixaSemRota === true ? (
        <Alert severity="warning" variant="outlined" role="none">
          A caixa de automação não tem núcleo configurado: nenhum e-mail está sendo triado.
        </Alert>
      ) : null}

      {resumo !== undefined && resumo.abertas > 0 ? (
        <Alert severity="warning" variant="outlined" role="none">
          <Stack direction="row" flexWrap="wrap" columnGap={1.5} rowGap={0.25} alignItems="baseline">
            <Typography variant="body2">{textoDoAviso(resumo)}</Typography>
            <LinkParaAsFalhas />
          </Stack>
          {doNucleo ? (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
              A contagem é só das caixas do seu núcleo.
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {/* Sinais silenciosos do parser/agente de IA: não viram `email_erro`, então não entram no aviso acima. */}
      {resumo !== undefined && textoDeLeituraSemAgente(resumo) !== null ? (
        <Alert severity="warning" variant="outlined" role="none">
          <Stack direction="row" flexWrap="wrap" columnGap={1.5} rowGap={0.25} alignItems="baseline">
            <Typography variant="body2">{textoDeLeituraSemAgente(resumo)}</Typography>
            <LinkParaAsFalhas />
          </Stack>
        </Alert>
      ) : null}

      {resumo !== undefined && textoDeLeituraDesistida(resumo) !== null ? (
        <Alert severity="warning" variant="outlined" role="none">
          <Stack direction="row" flexWrap="wrap" columnGap={1.5} rowGap={0.25} alignItems="baseline">
            <Typography variant="body2">{textoDeLeituraDesistida(resumo)}</Typography>
            <LinkParaAsFalhas />
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  );
}
